/**
 * Esc закрывает ВЕРХНЮЮ выезжающую панель — и только её.
 *
 * Панели кабинета ложатся стопкой: «инфо» товара → «в заказах» → карточка
 * заказа → карго-накладная (разметка 26.09.26 06:30 п.2). Пока каждая слушала
 * Esc сама, первой срабатывала та, что подписалась раньше, — то есть НИЖНЯЯ.
 * Здесь один слушатель на захвате окна и стопка слоёв в порядке открытия:
 * Esc зовёт закрытие верхнего и гасит событие, чтобы нижние его не видели.
 *
 * СЛОЙ ЖИВЁТ С МОНТИРОВАНИЯ ДО РАЗМОНТИРОВАНИЯ (аудит 12.10.26, B30/B62/B76).
 * Раньше Esc снимал слой сразу, и панель, чей обработчик закрыл лишь СВОЮ
 * форму (`PositionReceivedPanel`: сначала форма статуса, вторым Esc — панель),
 * теряла слой: второй Esc её уже не закрывал. Теперь решает обработчик:
 * вернул `false` — «не закрылся, оставь меня верхним»; иначе слой помечается
 * закрытым и следующий Esc идёт к нижнему — даже если перерисовка ещё не
 * сняла панель с экрана (второй Esc подряд до перерисовки — замер на домене
 * 26.09.26). Панель, которая прячется БЕЗ размонтирования, после своего Esc
 * второй раз не закроется — монтировать её условно либо передавать `active`.
 */

import { useEffect, useRef } from "react";

export type EscapeLayer = { close: () => void | boolean; closed: boolean };

/**
 * Esc для стопки: верхний НЕЗАКРЫТЫЙ слой зовётся и, если не вернул `false`,
 * помечается закрытым. Нет такого слоя — `null`, событие идёт дальше.
 * Чистая функция — её гоняет `web/checks/escapeStack.check.ts`.
 */
export function pressEscape(stack: EscapeLayer[]): EscapeLayer | null {
  for (let at = stack.length - 1; at >= 0; at -= 1) {
    const layer = stack[at]!;
    if (layer.closed) continue;
    if (layer.close() !== false) layer.closed = true;
    return layer;
  }
  return null;
}

const layers: EscapeLayer[] = [];

function onKey(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  if (!pressEscape(layers)) return;
  event.stopImmediatePropagation();
  event.preventDefault();
}

/**
 * Слой панели: пока компонент смонтирован (и `active`), Esc над ним зовёт
 * `onClose`. `onClose` возвращает `false`, когда закрыл только внутреннюю
 * ступень и панель остаётся. `active` — для меню, живущих в компоненте
 * постоянно (`SupplierName`): слой встаёт на открытие и уходит на закрытие.
 */
export function useEscapeLayer(onClose: () => void | boolean, active = true): void {
  // Ссылка, а не сама функция: слой держит место в стопке с монтирования, а
  // замыкание `onClose` меняется на каждом рендере.
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    if (!active) return;
    const layer: EscapeLayer = { close: () => ref.current(), closed: false };
    if (layers.length === 0) window.addEventListener("keydown", onKey, true);
    layers.push(layer);
    return () => {
      const at = layers.indexOf(layer);
      if (at >= 0) layers.splice(at, 1);
      if (layers.length === 0) window.removeEventListener("keydown", onKey, true);
    };
  }, [active]);
}
